import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, useScroll, useSpring, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "Manifesto", id: "manifesto", testId: "nav-link-manifesto" },
  { label: "Programs", id: "programs", testId: "nav-link-programs" },
  { label: "Results", id: "results", testId: "nav-link-results" },
  { label: "FAQ", id: "faq", testId: "nav-link-faq" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 26 });

  const goAnchor = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
      return;
    }
    if (window.__lenis) {
      window.__lenis.scrollTo(`#${id}`, { offset: -72, duration: 1.4 });
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const goPage = (to) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <header
      data-testid="nav-header"
      className="fixed top-0 left-0 right-0 z-[80] border-b border-[#27272A] bg-[#080808]/85 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
        <button
          data-testid="nav-logo"
          onClick={() => goPage("/")}
          className="flex items-center gap-2.5"
        >
          <span className="block h-3 w-3 bg-[#CC0A14]" />
          <span className="font-syne text-lg font-extrabold tracking-[0.18em] text-white">
            BP<span className="text-[#CC0A14]">//</span>CLUB
          </span>
        </button>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testId}
              onClick={() => goAnchor(l.id)}
              className="font-jb text-xs uppercase tracking-[0.22em] text-zinc-400 transition-colors duration-300 hover:text-white"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-link-journal"
            onClick={() => goPage("/journal")}
            className="font-jb text-xs uppercase tracking-[0.22em] text-zinc-400 transition-colors duration-300 hover:text-white"
          >
            Journal
          </button>
          <button
            data-testid="nav-link-method"
            onClick={() => goPage("/method")}
            className="font-jb text-xs uppercase tracking-[0.22em] text-zinc-400 transition-colors duration-300 hover:text-white"
          >
            Method
          </button>
          <button
            data-testid="nav-cta-button"
            onClick={() => goPage("/method")}
            className="border border-[#CC0A14] bg-[#CC0A14] px-5 py-2.5 font-jb text-xs font-semibold uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-transparent hover:text-[#CC0A14]"
          >
            Start the Protocol
          </button>
        </nav>

        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="text-white lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <motion.div
        className="h-[2px] origin-left bg-[#CC0A14]"
        style={{ scaleX: progress }}
      />

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="border-t border-[#27272A] bg-[#080808]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-6">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-${l.testId}`}
                  onClick={() => goAnchor(l.id)}
                  className="py-3 text-left font-syne text-xl font-bold uppercase tracking-wide text-zinc-300 transition-colors duration-300 hover:text-[#CC0A14]"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-nav-link-journal"
                onClick={() => goPage("/journal")}
                className="py-3 text-left font-syne text-xl font-bold uppercase tracking-wide text-zinc-300 transition-colors duration-300 hover:text-[#CC0A14]"
              >
                Journal
              </button>
              <button
                data-testid="mobile-nav-link-method"
                onClick={() => goPage("/method")}
                className="py-3 text-left font-syne text-xl font-bold uppercase tracking-wide text-zinc-300 transition-colors duration-300 hover:text-[#CC0A14]"
              >
                Method
              </button>
              <button
                data-testid="mobile-nav-cta-button"
                onClick={() => goPage("/method")}
                className="mt-4 border border-[#CC0A14] bg-[#CC0A14] px-5 py-3 font-jb text-xs font-semibold uppercase tracking-[0.22em] text-white"
              >
                Start the Protocol
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
